/**
 * The building footprints currently in memory, wherever they came from.
 *
 * Five things need to know where buildings are and how tall they are: the
 * rooftop lift for marks, the shading for buildings nothing is known about,
 * the police tint, the inferred doors, and the camera viewsheds. All five used
 * to read one array loaded up front, which is exactly what tiling removes.
 *
 * So they read this instead. Tiles add and remove their features as the map
 * moves; the whole-file path loads once and never changes. Neither caller
 * learns which happened.
 *
 * Everything here is scoped to what is loaded, and that is not a compromise —
 * a viewshed off screen is not drawn, a door off screen is not drawn, and a
 * mark you cannot see does not need its roof looked up. The one case that
 * genuinely wants more than the viewport is a mark on a building you have
 * panned away from, and its height is re-read the moment it comes back.
 */

/** Grid cell in degrees. Comfortably larger than any building. */
const CELL = 0.0008;

const cellKey = (x, y) => `${Math.floor(x / CELL)}:${Math.floor(y / CELL)}`;

/** Outer ring of a polygon, whatever nesting it arrived in. */
export function outerRing(polygon) {
  if (!Array.isArray(polygon)) return null;
  return Array.isArray(polygon[0]?.[0]) ? polygon[0] : polygon;
}

/** Ray-casting point-in-polygon. */
export function inRing(x, y, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Squared-ish distance from a point to a segment, in degrees.
 *
 * Degrees rather than metres on purpose: this only ever ranks candidates
 * against each other over a few tens of metres, and converting each one costs
 * more than the comparison is worth.
 */
function distanceToSegment(px, py, [ax, ay], [bx, by]) {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

export class FootprintIndex {
  constructor() {
    /** cell key -> array of {ring, height, id} */
    this.grid = new Map();
    /** source key -> the entries it contributed, so it can be withdrawn */
    this.sources = new Map();
    /** Bumped on every change, so layers can invalidate what they cached. */
    this.stamp = 0;
    this.count = 0;
  }

  /**
   * Add a batch of footprints under a key.
   *
   * The key is how they are removed again — a tile id for tiles, a constant
   * for the whole-file path. Adding the same key twice replaces rather than
   * duplicates, because deck.gl can re-load a tile it already had.
   */
  add(key, entries) {
    if (this.sources.has(key)) this.remove(key);

    const stored = [];
    for (const entry of entries) {
      const ring = outerRing(entry.polygon);
      if (!Array.isArray(ring) || ring.length < 3) continue;

      let west = Infinity; let east = -Infinity;
      let south = Infinity; let north = -Infinity;
      for (const point of ring) {
        if (point[0] < west) west = point[0];
        if (point[0] > east) east = point[0];
        if (point[1] < south) south = point[1];
        if (point[1] > north) north = point[1];
      }

      const record = { ring, height: entry.height, west, east, south, north };
      stored.push(record);

      for (let cx = Math.floor(west / CELL); cx <= Math.floor(east / CELL); cx += 1) {
        for (let cy = Math.floor(south / CELL); cy <= Math.floor(north / CELL); cy += 1) {
          const cell = `${cx}:${cy}`;
          let bucket = this.grid.get(cell);
          if (!bucket) this.grid.set(cell, (bucket = []));
          bucket.push(record);
        }
      }
    }

    this.sources.set(key, stored);
    this.count += stored.length;
    this.stamp += 1;
  }

  remove(key) {
    const stored = this.sources.get(key);
    if (!stored) return;
    const drop = new Set(stored);

    for (const [cell, bucket] of this.grid) {
      const kept = bucket.filter((record) => !drop.has(record));
      if (kept.length) this.grid.set(cell, kept);
      else this.grid.delete(cell);
    }

    this.sources.delete(key);
    this.count -= stored.length;
    this.stamp += 1;
  }

  clear() {
    this.grid.clear();
    this.sources.clear();
    this.count = 0;
    this.stamp += 1;
  }

  /**
   * Height of the tallest building covering a point, or 0 on open ground.
   *
   * Tallest rather than first: footprints overlap where a podium carries a
   * tower, and a beacon should stand on the tower.
   */
  heightAt(point) {
    const lng = point?.lng ?? point?.[0];
    const lat = point?.lat ?? point?.[1];
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return 0;

    let tallest = 0;
    for (const record of this.grid.get(cellKey(lng, lat)) ?? []) {
      if (lng < record.west || lng > record.east) continue;
      if (lat < record.south || lat > record.north) continue;
      if (record.height > tallest && inRing(lng, lat, record.ring)) {
        tallest = record.height;
      }
    }
    return tallest;
  }

  /**
   * The nearest wall to a point, as the two ends of that footprint edge.
   *
   * Doors from OSM are bare points with no indication of which way the wall
   * runs, so an extruded door slab has nothing to orient against and ends up
   * lying across the doorway instead of sitting in it. The building's own
   * geometry is the answer, and the grid already has it to hand.
   *
   * Only the cell the point falls in and its neighbours are searched — a door
   * sits within a metre or so of its wall, and walking every footprint in the
   * city to find that is not a trade worth making.
   */
  edgeNear(lng, lat) {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;

    const cx = Math.floor(lng / CELL);
    const cy = Math.floor(lat / CELL);
    let best = null;
    let bestDist = Infinity;

    for (let ix = cx - 1; ix <= cx + 1; ix += 1) {
      for (let iy = cy - 1; iy <= cy + 1; iy += 1) {
        for (const record of this.grid.get(`${ix}:${iy}`) ?? []) {
          const ring = record.ring;
          for (let i = 0; i < ring.length - 1; i += 1) {
            const d = distanceToSegment(lng, lat, ring[i], ring[i + 1]);
            if (d < bestDist) {
              bestDist = d;
              best = [ring[i], ring[i + 1]];
            }
          }
        }
      }
    }
    if (!best) return null;

    // The caller gets the wall AND the point on it closest to the query —
    // an OSM door node and this index's surveyed outline can disagree by
    // metres, and "which wall" without "where on it" leaves the door
    // floating that far inside or outside the extrusion.
    const [a, b] = best;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const lenSq = dx * dx + dy * dy;
    let t = lenSq === 0 ? 0 : ((lng - a[0]) * dx + (lat - a[1]) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const point = [a[0] + t * dx, a[1] + t * dy];
    // Metres, near enough: a degree of latitude is ~111km everywhere, a
    // degree of longitude ~85km at Denver's latitude.
    const meters = Math.hypot((lng - point[0]) * 85400, (lat - point[1]) * 111320);
    return { a, b, point, meters };
  }

  /** Is any building covering this point? Cheaper than asking how tall. */
  blocked(lng, lat) {
    for (const record of this.grid.get(cellKey(lng, lat)) ?? []) {
      if (lng < record.west || lng > record.east) continue;
      if (lat < record.south || lat > record.north) continue;
      if (inRing(lng, lat, record.ring)) return true;
    }
    return false;
  }

  /** Every footprint currently loaded, for callers that must walk them all. */
  *all() {
    for (const stored of this.sources.values()) {
      yield* stored;
    }
  }
}

/** One index for the app; layers and the HUD share it. */
export const footprints = new FootprintIndex();
